import React, { useCallback, useEffect, useState } from 'react';
import { Box, Flex, Text, Button, Card, Badge, Heading, TextField, Callout } from '@radix-ui/themes';
import { ReloadIcon, ExclamationTriangleIcon, CheckCircledIcon, InfoCircledIcon } from '@radix-ui/react-icons';
import toast from 'react-hot-toast';
import { smartmeterApi, SmartMeterStatus } from '../../services/api';

/**
 * PHA-1103 — SmartMeter operations panel.
 *
 * This integration's failure mode is silence, not errors. SmartMeter drops an
 * unacked webhook delivery after 60 minutes, a device registered under the wrong
 * id produces readings that attribute to nobody, and a reading type we cannot
 * map is skipped — none of which surfaces anywhere on its own. So the panel
 * answers the three questions an operator actually needs:
 *
 *   1. Is the vendor configured and pointed at us? (credentials + notify_url)
 *   2. Is data arriving, and how stale is it?
 *   3. What is being dropped, and whose readings are going nowhere?
 *
 * Plus the manual recovery lever: pull a window of readings back on demand.
 */

const RECONCILE_PRESETS = [
  { label: 'Last 6 hours', hours: 6 },
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Last 7 days', hours: 24 * 7 },
];

const STALE_AFTER_HOURS = 24;

const formatWhen = (value: string | null | undefined): string => {
  if (!value) return 'never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return date.toLocaleString();
};

const hoursSince = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return (Date.now() - date.getTime()) / (1000 * 60 * 60);
};

const SmartMeterPanel: React.FC = () => {
  const [status, setStatus] = useState<SmartMeterStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [notifyUrl, setNotifyUrl] = useState('');
  const [registeredUrl, setRegisteredUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await smartmeterApi.getStatus());
    } catch (error: any) {
      toast.error(`Failed to load SmartMeter status: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const result = await smartmeterApi.getNotifications();
      const reading = (result.notifications || []).find((n: any) => (n.type || 'reading') === 'reading');
      setRegisteredUrl(reading?.notify_url || null);
    } catch {
      // Expected whenever credentials are absent; the status card already says so.
      setRegisteredUrl(null);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchNotifications();
  }, [fetchStatus, fetchNotifications]);

  const handleRegisterWebhook = async () => {
    if (!/^https:\/\//i.test(notifyUrl.trim())) {
      toast.error('Enter the full https URL of the webhook endpoint');
      return;
    }
    setBusy(true);
    try {
      await smartmeterApi.registerNotification(notifyUrl.trim(), 'reading');
      toast.success('Webhook URL registered with SmartMeter');
      setNotifyUrl('');
      await fetchNotifications();
    } catch (error: any) {
      toast.error(`Failed to register webhook: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleReconcile = async (hours: number, label: string) => {
    setBusy(true);
    const pending = toast.loading(`Pulling readings (${label})...`);
    try {
      const result = await smartmeterApi.reconcile({ hours });
      const t = result?.totals;
      toast.success(
        t
          ? `${label}: ${t.received} received, ${t.stored} new, ${t.duplicates} already had, ${t.skipped} skipped`
          : `${label}: done`,
        { id: pending, duration: 8000 }
      );
      await fetchStatus();
    } catch (error: any) {
      toast.error(`Reconcile failed: ${error.message}`, { id: pending });
    } finally {
      setBusy(false);
    }
  };

  const skipped = status ? Object.entries(status.skippedSinceStart) : [];
  const lastReceived = status?.readingsByType
    .map((r) => r.last_received)
    .sort()
    .pop();
  const staleHours = hoursSince(lastReceived);

  return (
    <Card>
      <Flex direction="column" gap="4">
        <Flex align="center" justify="between">
          <Box>
            <Heading size="3">SmartMeter</Heading>
            <Text size="1" color="gray">
              Readings share the Tellihealth tables, so they appear on the patient chart and in RPM billing automatically.
            </Text>
          </Box>
          <Button variant="soft" size="1" onClick={fetchStatus} disabled={loading}>
            <ReloadIcon />
            Refresh
          </Button>
        </Flex>

        {/* 1. Configuration */}
        {status && !status.configured && (
          <Callout.Root color="amber">
            <Callout.Icon><ExclamationTriangleIcon /></Callout.Icon>
            <Callout.Text>
              <strong>SMARTMETER_API_KEY is not set.</strong> The reconciliation sweep is not running, so any
              webhook delivery missed during a restart is lost permanently after 60 minutes. Set the API key and
              secret key from the SmartMeter portal (API → Credentials).
            </Callout.Text>
          </Callout.Root>
        )}
        {status?.configured && !status.webhookSecretConfigured && (
          <Callout.Root color="red">
            <Callout.Icon><ExclamationTriangleIcon /></Callout.Icon>
            <Callout.Text>
              <strong>SMARTMETER_WEBHOOK_SECRET is not set.</strong> Every incoming reading is being rejected with
              401 — the receiver cannot verify the X-Server-Signature without the account's <em>secret</em> key
              (not the API key).
            </Callout.Text>
          </Callout.Root>
        )}

        {/* 2. Is anything arriving? */}
        {status && (
          <Box>
            <Flex align="center" gap="2" mb="2">
              <Text size="2" weight="bold">Readings received</Text>
              {staleHours === null ? (
                <Badge color="gray">none yet</Badge>
              ) : staleHours > STALE_AFTER_HOURS ? (
                <Badge color="red">stale — {Math.floor(staleHours)}h since last</Badge>
              ) : (
                <Badge color="green">fresh</Badge>
              )}
            </Flex>
            <Flex direction="column" gap="1">
              {status.readingsByType.length === 0 && (
                <Text size="1" color="gray">No SmartMeter readings stored yet.</Text>
              )}
              {status.readingsByType.map((r) => (
                <Flex key={r.reading_type} justify="between" align="center">
                  <Badge variant="soft">{r.reading_type}</Badge>
                  <Flex gap="3" align="center">
                    <Text size="1">{r.count} readings</Text>
                    <Text size="1" color="gray">Last: {formatWhen(r.last_received)}</Text>
                  </Flex>
                </Flex>
              ))}
            </Flex>
            <Text size="1" color="gray" mt="2" as="div">
              {status.devices.registered} SmartMeter device(s) registered, {status.devices.active} active.
            </Text>
          </Box>
        )}

        {/* 3. What is being dropped or going nowhere? */}
        {status && status.orphanDevices.length > 0 && (
          <Callout.Root color="amber">
            <Callout.Icon><ExclamationTriangleIcon /></Callout.Icon>
            <Callout.Text>
              <strong>{status.orphanDevices.length} device(s) sending readings that belong to no patient.</strong>{' '}
              These readings are stored but invisible on every chart and excluded from RPM billing until the device
              is linked. Check the device id matches the physical unit — never guess the patient.
              <Box mt="2">
                {status.orphanDevices.slice(0, 10).map((d) => (
                  <Text key={d.device_id} size="1" as="div">
                    {d.device_id} — {d.count} reading(s), last {formatWhen(d.last_reading)}
                  </Text>
                ))}
              </Box>
            </Callout.Text>
          </Callout.Root>
        )}

        {skipped.length > 0 && (
          <Box>
            <Text size="2" weight="bold">Skipped since server start</Text>
            <Text size="1" color="gray" as="div" mb="1">
              Readings the vendor sent that could not be stored. FibriCheck has no equivalent reading type here and
              is dropped on purpose.
            </Text>
            {skipped.map(([reason, count]) => (
              <Flex key={reason} justify="between">
                <Text size="1">{reason}</Text>
                <Text size="1" weight="bold">{count}</Text>
              </Flex>
            ))}
          </Box>
        )}

        {/* Webhook registration */}
        <Box>
          <Text size="2" weight="bold" as="div" mb="1">Webhook endpoint</Text>
          {registeredUrl ? (
            <Flex align="center" gap="2" mb="2">
              <CheckCircledIcon color="green" />
              <Text size="1" style={{ wordBreak: 'break-all' }}>{registeredUrl}</Text>
            </Flex>
          ) : (
            <Text size="1" color="gray" as="div" mb="2">
              No reading webhook registered with SmartMeter (or credentials unavailable).
            </Text>
          )}
          <Callout.Root size="1" mb="2">
            <Callout.Icon><InfoCircledIcon /></Callout.Icon>
            <Callout.Text>
              SmartMeter allows exactly one reading endpoint per account, so registering here replaces whatever is
              set — including another environment's. The path is <code>/webhooks/smartmeter/readings</code>.
            </Callout.Text>
          </Callout.Root>
          <Flex gap="2">
            <Box style={{ flex: 1 }}>
              <TextField.Root>
                <TextField.Input
                  value={notifyUrl}
                  onChange={(e) => setNotifyUrl(e.currentTarget.value)}
                  placeholder="https://ccm.phanova.com/webhooks/smartmeter/readings"
                />
              </TextField.Root>
            </Box>
            <Button onClick={handleRegisterWebhook} disabled={busy || !status?.configured}>
              Register
            </Button>
          </Flex>
        </Box>

        {/* Manual recovery */}
        <Box>
          <Text size="2" weight="bold" as="div" mb="1">Pull readings from SmartMeter</Text>
          <Text size="1" color="gray" as="div" mb="2">
            Re-reads by ingest date and stores anything missing. Safe to run repeatedly — readings already stored
            are ignored, not duplicated. This runs automatically every 30 minutes; use it after an outage or to
            confirm nothing was lost.
          </Text>
          <Flex gap="2" wrap="wrap">
            {RECONCILE_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant="soft"
                size="2"
                disabled={busy || !status?.configured}
                onClick={() => handleReconcile(preset.hours, preset.label)}
              >
                {preset.label}
              </Button>
            ))}
          </Flex>
        </Box>
      </Flex>
    </Card>
  );
};

export default SmartMeterPanel;
